import Button from "@/ui/Buttons/Button";
import Auth from "@/Auth/Auth";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Icons from "@/ui/IconButton/Icons";
import React, {useCallback, useLayoutEffect, useState} from "react";
import type {RootState} from "@/@types/store";
import {confirmWarningOkCancel} from '@/ui/Confirm';
import {doFullRefresh} from '@/queues/polling';
import localforage from 'localforage';
import * as idb from "@/lib/idbd";
import type {StoreResult} from "@/lib/idbd";
import {connect} from "react-redux";
import Alert from "@/lib/alert";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";
import type {EntityType} from "@/@types/entity";
import {cast} from "@/entities/schemas";
import util from "@/lib/util";
import {processQueue} from "@/queues/processQueue";
import {EnvironmentResult} from "../Support";
import useComponentCache from "@/hooks/useComponentCache";

type CacheManagementState = {
    isClearingBackups: boolean,
}

function CacheManagement(props: CacheManagementProps) {
    const {cacheValue, cacheSave} = useComponentCache<CacheManagementState>('cache-management');

    const [isClearingBackups, setIsClearingBackups] = useState<boolean>(cacheValue('isClearingBackups', false));

    const isServerOnline = props.environment?.connectivity.isApiServerConnected;
    const isInternetOnline = props.environment?.connectivity.isOnline;

    // Save cache
    useLayoutEffect(() => {
        return () => {
            cacheSave({isClearingBackups});
        }
    }, [cacheSave, isClearingBackups]);

    /**
     * Get queues
     *
     * @param {StoreResult} queues
     */
    const getWarnings = useCallback((queues: StoreResult) => {
        // Check if there are any uncommitted items in the queues.  If so, report the number and type to the user
        // as a warning, and color the Clear Caches button appropriately
        const cacheKeys = Object.keys(queues);

        return cacheKeys.reduce((found, key) => {
            const entries = queues[key];
            if (Array.isArray(entries) && entries.length > 0) {
                return found.concat([{key, entries}]);
            }
            else return found;
        }, []);
    }, []);

    const renderWarningCell = useCallback((key: string, entries: any[]) => {
        const numValues = entries.length;

        const castedEntries: EntityType[] = entries.map(t => cast(key, t));

        return (
            <tr className='table-warning' key={key}>
                {/* Type */}
                <td>{util.capitalize(key)}</td>

                {/* # Entries */}
                <td className='table-centered'>{numValues}</td>

                {/* Description */}
                <td className='table-icons'>
                    {entries.map((entry, i) => {
                        let label = entry.displayFn?.() || entry.name || entry.label || entry.invoiceNumber || key;
                        if (typeof (label) === 'object') label = JSON.stringify(label);
                        return <div key={`warning-cell-${entry.id || entry.addedOn}-${i}`} style={{marginBottom: 5, color: entry.fatalError?.message ? 'var(--sl-color-danger-600)' : 'inherit'}}>{label}</div>;
                    })}
                </td>

                {/* Errors */}
                <td className='table-errors'>
                    {castedEntries.map((entry, i) => {
                        if (entry.fatalError?.message && !entry.fatalError.recoverable) {
                            return <div key={`warning-cell-casted-${entry?.id || entry?.addedOn}-${i}`} style={{marginBottom: 5, minWidth: '10em'}}>{entry.fatalError.message}</div>
                        }
                        else return <div key={`warning-cell-casted-${entry?.id || entry?.addedOn}-${i}`} style={{marginBottom: 5, minWidth: '10em'}}>&nbsp;</div>;
                    })}
                </td>
            </tr>
        );
    }, []);

    // Get any warning for the storage
    const warnings = getWarnings(props.environment.storage.queues);

    // What is cached?
    const cacheTable = warnings.length ? warnings.map(w => renderWarningCell(w.key, w.entries)) : undefined;

    // Get the total number of warnings
    const numEntries = warnings.reduce((total, w) => total + w.entries.length, 0);

    const onRetryCache = useCallback(async () => {
        // Strip error information from all objects in the cache and try to save them to the database.
        const result = await idb.resetAll();
        if (result.isOk()) await processQueue();
        else Alert.addSimpleError(result.unwrapErr().message);
    }, []);

    // Construct a warning
    let clearCacheWarning = (
        <div>
            <div>Clear out any changes that have yet to be uploaded to the cloud.</div>
            <div>This includes not just new items, but changes and deletes you have made to existing items.
            </div>
            {warnings.length > 0 &&
				<div
					className='support-form--cache-warning'>{`You have ${numEntries} uncommitted entr${numEntries > 1 ? 'ies' : 'y'} in the cache (see below)`}</div>
            }
        </div>
    );


    /**
     * Reload data from the server
     */
    const onRefreshData = useCallback(() => {
        doFullRefresh('onRefreshData');
    }, []);

    /**
     * Clear out anything in IndexedDB
     */
    const onClearUncommittedChanges = useCallback(() => {
        // Warn the user that we are removing precious data
        confirmWarningOkCancel(
            'Clear uncommitted changes?',
            <p>About to clear uncommitted changes. This action can not be undone.</p>,
            'OK', 'Cancel',
            () => {
                idb.clearErrors()
                    .then(clearResult => {
                        if (clearResult.isErr()) Alert.addSimpleError(clearResult.unwrapErr().message);
                    })
                    .catch(console.error);
            },
            () => {
            }
        );
    }, []);

    const onClearBackups = useCallback(async () => {
        setIsClearingBackups(true);

        document.body.style.cursor = 'wait';

        try {
            const firmId = getCurrentFirmId();
            const userId = getCurrentUserId();

            if (firmId && userId) {
                // Just clear out the backups IDB
                // Set up localForage
                await localforage.dropInstance({
                    name: `Backups_${firmId}_${userId}`
                });

                // Set up backup
                localforage.config({
                    name: `Backups_${firmId}_${userId}`,
                    driver: localforage.INDEXEDDB,
                    description: `Redux backups for ${firmId}/${userId}`
                });

                // Clear out the last partial
                doFullRefresh('onClearBackups', () => {
                    setIsClearingBackups(false);
                    document.body.style.cursor = 'default';
                });
            }
            else {
                // Clear out ALL backups for all firms

                const stores = (await idb.getStoreNames()).filter(store => store.name.startsWith('Backup'));

                for (let i = 0; i < stores.length; i++) {
                    const {name} = stores[i];
                    await localforage.dropInstance({
                        name: name
                    });

                    const nameParts = name.split('_');

                    localforage.config({
                        name,
                        driver: localforage.INDEXEDDB,
                        description: `Redux backups for ${nameParts[1]}/${nameParts[2]}`
                    });
                }

                setIsClearingBackups(false);
                document.body.style.cursor = 'default';
            }
        }
        catch (err) {
            setIsClearingBackups(false);
            document.body.style.cursor = 'default';
        }
    }, [setIsClearingBackups]);

    /**
     * Clear out the application cache and local/session storage
     *
     * @param e
     */
    const onClearCaches = useCallback(() => {
        // Warn the user that we are removing precious data
        confirmWarningOkCancel(
            'Clear uncommitted changes?',
            <p>About to clear all caches and storage. This action can not be undone.</p>,
            'OK', 'Cancel',
            () => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
            },
            () => {
            }
        );
    }, []);

    return <section id='cache-management'>
        {/* Refresh Data from Cloud */}
        <article className="support-form__buttons">
            <Button label="Refresh Data"
                    severity={props.statusBar.isFetching ? 'warning' : 'success'}
                    onClick={onRefreshData}
                    disabled={!isServerOnline || !isInternetOnline || !Auth.isAuthenticated() || props.statusBar.isFetching}
            />
            <aside>
                {props.statusBar.isFetching && <div>Loading...</div>}
                {!props.statusBar.isFetching &&
					<div>Starts a background reload of all data
						from the cloud. This is the same as clicking on the <FontAwesomeIcon icon={Icons.SYNC_LIGHT.reactIcon} color='#D66C16'/> Sync
						button in the toolbar.
					</div>
                }
            </aside>
        </article>

        {/* Only clear the backup cache */}
        <article className="support-form__buttons">
            <Button label="Clear Backups"
                    severity='warning'
                    onClick={onClearBackups}
                    disabled={isClearingBackups}
            />
            <aside>
                This will clear the backups used to make Outlaw Practice load quickly.
                You will not be logged out, and a full refresh of data will start immediately.
            </aside>
        </article>

        {/* Clear application cache and local storage */}
        <article className="support-form__buttons">
            <Button label="Clear Caches"
                    severity='danger'
                    onClick={onClearCaches}
            />
            <aside>
                This will clear all caches and storage used by <strong>only</strong> this application.
                You will also be logged out and brought back to this page.
            </aside>
        </article>

        {/* Clear uncommitted changes */}
        <article className="support-form__buttons">
            <Button label="Clear Uncommitted"
                    severity='danger'
                    onClick={onClearUncommittedChanges}
                    disabled={!warnings.length}
            />
            <aside>{clearCacheWarning}</aside>
        </article>

        <hr/>

        {/* Cached entries */}
        <ErrorBoundaryO uniqueKey='cached-entries'>
            <article>
                <h3>Uncommitted Entries</h3>
                {(cacheTable?.length > 0) && <div style={{marginBottom: '1em'}}>
					<Button label='Retry Cache' severity='warning' onClick={onRetryCache}/>
				</div>}

                {cacheTable && (cacheTable.length > 0) ?
                 <div className="support-form__table">
                     <table>
                         <thead>
                         <tr>
                             <th>Type</th>
                             <th>Entries</th>
                             <th>Description</th>
                             <th>Errors</th>
                         </tr>
                         </thead>
                         <tbody>{cacheTable}</tbody>
                     </table>
                 </div> :
                 <aside style={{color: 'darkgreen'}}>No entries in the cache</aside>
                }
            </article>
        </ErrorBoundaryO>
    </section>
}

const mapStateToProps = (state: RootState) => ({
    statusBar: state.statusBar,
});

export type CacheManagementProps = Pick<RootState, 'statusBar'> & {
    environment: EnvironmentResult;
}

export default connect(mapStateToProps)(CacheManagement);